import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  increment,
} from "firebase/firestore";
import { db } from "../api/firebase";

export interface ReferralCodeData {
  interviewCount: number;
}

export interface ReferralCodeValidationResult {
  valid: boolean;
  code?: string;
  interviewCount?: number;
  currentUserInterviewCount?: number;
  willIncreaseCount?: boolean;
  message: string;
}

/**
 * Validates a referral code and returns validation result
 */
export async function validateReferralCode(
  code: string,
  userId?: string
): Promise<ReferralCodeValidationResult> {
  if (!code || code.trim() === "") {
    return {
      valid: false,
      message: "Referral code is required",
    };
  }

  try {
    // Capitalize the referral code
    const capitalizedCode = code.trim().toUpperCase();

    // Check if referral code exists
    const referralCodeRef = doc(db, "referralCodes", capitalizedCode);
    const referralCodeDoc = await getDoc(referralCodeRef);

    if (!referralCodeDoc.exists()) {
      return {
        valid: false,
        message: "Referral code not found",
      };
    }

    const referralData = referralCodeDoc.data() as ReferralCodeData;

    // If userId is provided, check current user's interview count
    let currentInterviewCount = 0;
    let willIncrease = false;

    if (userId) {
      const userRef = doc(db, "users", userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        currentInterviewCount = userData.interviewCount || 0;
        willIncrease = referralData.interviewCount > currentInterviewCount;
      }
    }

    return {
      valid: true,
      code: capitalizedCode,
      interviewCount: referralData.interviewCount,
      currentUserInterviewCount: currentInterviewCount,
      willIncreaseCount: willIncrease,
      message: willIncrease
        ? `This code will increase your interview count from ${currentInterviewCount} to ${referralData.interviewCount}`
        : currentInterviewCount >= referralData.interviewCount
        ? `Your current interview count (${currentInterviewCount}) is already higher than or equal to this code's benefit (${referralData.interviewCount})`
        : `This code provides ${referralData.interviewCount} interviews`,
    };
  } catch (error) {
    console.error("Error validating referral code:", error);
    return {
      valid: false,
      message: "Error validating referral code",
    };
  }
}

/**
 * Applies a referral code to a user's account
 */
export async function applyReferralCode(
  userId: string,
  referralCode: string,
  surveyData: any
): Promise<{
  success: boolean;
  message: string;
  interviewCountUpdated: boolean;
}> {
  try {
    // Capitalize the referral code
    const capitalizedCode = referralCode.trim().toUpperCase();

    // First validate the referral code
    const validation = await validateReferralCode(capitalizedCode, userId);

    if (!validation.valid) {
      // Still complete the survey but don't apply referral code
      await updateUserSurvey(
        userId,
        surveyData,
        capitalizedCode,
        "Referral code not valid"
      );
      return {
        success: true,
        message:
          "Survey completed, but referral code was not applied: " +
          validation.message,
        interviewCountUpdated: false,
      };
    }

    // Get current user data
    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.exists() ? userDoc.data() : {};
    const currentInterviewCount = userData.interviewCount || 0;

    // Prepare user update data
    const userUpdate: any = {
      finishSurvey: true,
      surveyData: surveyData,
      surveyCompletedAt: serverTimestamp(),
    };

    // Only update if referral code's count is higher than user's current count
    if (validation.interviewCount! > currentInterviewCount) {
      userUpdate.interviewCount = validation.interviewCount;
      userUpdate.referralCodeUsed = capitalizedCode;
      userUpdate.referralCodeAppliedAt = serverTimestamp();
      userUpdate.previousInterviewCount = currentInterviewCount;

      await setDoc(userRef, userUpdate, { merge: true });

      return {
        success: true,
        message: `Survey completed successfully! Your interview count has been increased from ${currentInterviewCount} to ${validation.interviewCount}`,
        interviewCountUpdated: true,
      };
    } else {
      userUpdate.referralCodeAttempted = capitalizedCode;
      userUpdate.referralCodeAttemptedAt = serverTimestamp();
      userUpdate.referralCodeNotAppliedReason =
        "Current interview count is already higher or equal";

      await setDoc(userRef, userUpdate, { merge: true });

      return {
        success: true,
        message:
          "Survey completed successfully, but your current interview count is already higher than the referral code benefit",
        interviewCountUpdated: false,
      };
    }
  } catch (error) {
    console.error("Error applying referral code:", error);

    // Still try to complete the survey
    try {
      await updateUserSurvey(
        userId,
        surveyData,
        referralCode.trim().toUpperCase(),
        "Error processing referral code"
      );
    } catch (surveyError) {
      console.error("Error completing survey:", surveyError);
    }

    return {
      success: false,
      message: "Error processing referral code",
      interviewCountUpdated: false,
    };
  }
}

/**
 * Helper function to update user survey data
 */
async function updateUserSurvey(
  userId: string,
  surveyData: any,
  referralCode?: string,
  referralError?: string
) {
  const userRef = doc(db, "users", userId);
  const userUpdate: any = {
    finishSurvey: true,
    surveyData: surveyData,
    surveyCompletedAt: serverTimestamp(),
  };

  if (referralCode) {
    userUpdate.referralCodeAttempted = referralCode.trim().toUpperCase();
    userUpdate.referralCodeAttemptedAt = serverTimestamp();
    userUpdate.referralCodeNotAppliedReason = referralError || "Unknown error";
  }

  await setDoc(userRef, userUpdate, { merge: true });
}

/**
 * Creates sample referral codes (for testing)
 */
export async function createSampleReferralCodes() {
  const sampleCodes = [
    {
      code: "WELCOME10",
      interviewCount: 10,
    },
    {
      code: "PREMIUM20",
      interviewCount: 20,
    },
    {
      code: "STUDENT5",
      interviewCount: 5,
    },
    {
      code: "VIP50",
      interviewCount: 50,
    },
    {
      code: "TRIAL3",
      interviewCount: 3,
    },
  ];

  try {
    for (const codeData of sampleCodes) {
      const codeRef = doc(db, "referralCode", codeData.code);
      const existingDoc = await getDoc(codeRef);

      if (!existingDoc.exists()) {
        await setDoc(codeRef, {
          interviewCount: codeData.interviewCount,
        });
        console.log(
          `Created referral code: ${codeData.code} with ${codeData.interviewCount} interviews`
        );
      } else {
        console.log(`Referral code already exists: ${codeData.code}`);
      }
    }
    console.log("Sample referral codes creation completed");
  } catch (error) {
    console.error("Error creating sample referral codes:", error);
  }
}
